import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ActionModule } from '@typesApp/ActionModule'
import { CustomerContext } from '@typesApp/Customer'
import { HostEnvironment } from '@typesApp/Environment'
import { useSessionInfo } from '@hooks/useSessionInfo'
import {
  connectorActionModuleSelector,
  connectorEnvironmentSelector,
  connectorInitActionModule,
  connectorInitEnvironment,
  connectorInitCustomerContext,
  connectorCustomerContextSelector,
} from '@redux/slices/connectorSlice'
import { shouldInitCustomerContext } from '@utils/customerContextUtil'
import { initGlobalLocale, isEnvironmentChanged } from '../util/appInitUtil'

type UseAppInitParams = {
  customerContext: CustomerContext
  environment: HostEnvironment
  actionModule: ActionModule
}

/**
 * Initialize redux with the already sanitized configuration received from widget render
 */
export const useAppInit = (params: UseAppInitParams) => {
  const { customerContext, environment, actionModule } = params
  const dispatch = useDispatch()
  const { isTokenError, isSessionInitializing } = useSessionInfo()

  const currentEnvironment = useSelector(connectorEnvironmentSelector)
  const currentActionModule = useSelector(connectorActionModuleSelector)
  const currentCustomerContext = useSelector(connectorCustomerContextSelector)

  useEffect(() => {
    if (environment && (!currentEnvironment || isEnvironmentChanged(currentEnvironment, environment))) {
      dispatch(connectorInitEnvironment(environment))
      initGlobalLocale(environment)
    }
  }, [dispatch, environment, currentEnvironment])

  useEffect(() => {
    if (actionModule && !currentActionModule) {
      dispatch(connectorInitActionModule(actionModule))
    }
  }, [dispatch, actionModule, currentActionModule])

  const shouldInit = shouldInitCustomerContext({
    isTokenError,
    inProgress: isSessionInitializing,
    customerContext,
    currentCustomerContext,
  })
  useEffect(() => {
    if (shouldInit && customerContext) {
      dispatch(connectorInitCustomerContext(customerContext))
    }
  }, [dispatch, shouldInit, customerContext])
}
